import type { FC, ReactNode } from "react";
import type { FieldError as RhfFieldError } from "react-hook-form";

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel
} from "@/components/ui/field";
import { cn } from "@/lib/utils";

/**
 * Required marker as a pseudo-element, so the accessible label (and
 * `getByLabelText`) stays exactly the label text.
 */
export const requiredLabelClass =
  "after:ml-0.5 after:text-destructive after:content-['*']";

/** Full width and content height; `FormRow` distributes width for its children. */
export const fieldClass = "w-full min-w-0";

type Props = {
  id: string;
  label: ReactNode;
  required?: boolean;
  description?: ReactNode;
  error?: RhfFieldError;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
};

/** Label / control / description / error in the standard vertical layout. */
export const FieldShell: FC<Props> = ({
  id,
  label,
  required,
  description,
  error,
  disabled,
  className,
  children
}) => (
  <Field
    className={cn(fieldClass, className)}
    data-disabled={disabled || undefined}
    data-invalid={error ? true : undefined}
  >
    <FieldLabel htmlFor={id} className={cn(required && requiredLabelClass)}>
      {label}
    </FieldLabel>
    {children}
    {description ? <FieldDescription>{description}</FieldDescription> : null}
    {error ? <FieldError errors={[error]} /> : null}
  </Field>
);
